import { useState, useEffect, useCallback } from 'react';
import { inboxAPI } from '../../services/api';
import { Table, Badge, Button, Modal, Pagination, Spinner } from '../../components/ui';

const TABS = [
  { key: 'messages',  label: 'Contact Messages' },
  { key: 'volunteer', label: 'Volunteers' },
  { key: 'partner',   label: 'Partners' },
  { key: 'intern',    label: 'Internships' },
  { key: 'career',    label: 'Careers' },
  { key: 'client',    label: 'Clients' },
  { key: 'proposal',  label: 'Proposals' },
];

export default function InboxPage() {
  const [tab, setTab]         = useState('messages');
  const [data, setData]       = useState([]);
  const [pagination, setPag]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [selected, setSel]    = useState(null);
  const [saving, setSaving]   = useState(false);
  const [reviewForm, setRF]   = useState({ status: '', notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (tab === 'messages') { res = await inboxAPI.messages({ page }); }
      else { res = await inboxAPI.applications({ page, type: tab }); }
      setData(res.data.data || []); setPag(res.data.pagination);
    } catch { setData([]); } finally { setLoading(false); }
  }, [tab, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [tab]);

  const openDetail = (row) => { setSel(row); setRF({ status: row.status, notes: row.notes || '' }); };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      if (tab === 'messages') await inboxAPI.updateMessage(selected.id, { status: reviewForm.status });
      else await inboxAPI.updateApp(selected.id, reviewForm);
      setSel(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Update failed'); } finally { setSaving(false); }
  };

  const MSG_COLS = [
    { key: 'full_name', label: 'Sender', render: (v, row) => <div><div style={{ fontWeight: 500 }}>{v}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.email}</div></div> },
    { key: 'subject', label: 'Subject', render: v => <span style={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '—'}</span> },
    { key: 'status', label: 'Status', render: v => <Badge status={v} /> },
    { key: 'created_at', label: 'Received', render: v => new Date(v).toLocaleDateString() },
  ];

  const APP_COLS = [
    { key: 'full_name', label: 'Applicant', render: (v, row) => <div><div style={{ fontWeight: 500 }}>{v}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.email}{row.organization ? ` · ${row.organization}` : ''}</div></div> },
    { key: 'status', label: 'Status', render: v => <Badge status={v} /> },
    { key: 'created_at', label: 'Applied', render: v => new Date(v).toLocaleDateString() },
  ];

  const tabBtn = (t) => ({
    padding: '9px 16px', fontSize: 13, cursor: 'pointer', background: 'none', border: 'none',
    borderBottom: `2px solid ${tab === t ? 'var(--accent)' : 'transparent'}`,
    color: tab === t ? 'var(--accent)' : 'var(--text-muted)',
    fontWeight: tab === t ? 600 : 400, whiteSpace: 'nowrap', marginBottom: -1, transition: 'all 0.15s',
  });

  const MSG_STATUSES = ['new', 'read', 'responded', 'archived'];
  const APP_STATUSES = ['pending', 'reviewing', 'approved', 'rejected'];
  const statuses = tab === 'messages' ? MSG_STATUSES : APP_STATUSES;

  // Detail fields by tab type
  const renderDetailBody = () => {
    if (!selected) return null;
    if (tab === 'messages') {
      return (
        <>
          <DetailRow label="From"    value={`${selected.full_name} — ${selected.email}`} />
          {selected.phone   && <DetailRow label="Phone"   value={selected.phone} />}
          {selected.subject && <DetailRow label="Subject" value={selected.subject} />}
          <DetailRow label="Message" value={selected.message} multiline />
          <DetailRow label="Received" value={new Date(selected.created_at).toLocaleString()} />
        </>
      );
    }
    return (
      <>
        <DetailRow label="Name"  value={selected.full_name} />
        <DetailRow label="Email" value={selected.email} />
        {selected.phone        && <DetailRow label="Phone"        value={selected.phone} />}
        {selected.organization && <DetailRow label="Organization" value={selected.organization} />}
        {selected.position     && <DetailRow label="Position"     value={selected.position} />}
        {selected.location     && <DetailRow label="Location"     value={selected.location} />}
        {selected.linkedin_url && <DetailRow label="LinkedIn"     value={<a href={selected.linkedin_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{selected.linkedin_url}</a>} />}
        {selected.portfolio_url && <DetailRow label="Portfolio"   value={<a href={selected.portfolio_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{selected.portfolio_url}</a>} />}
        {selected.website_url  && <DetailRow label="Website"      value={<a href={selected.website_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{selected.website_url}</a>} />}
        {selected.message      && <DetailRow label="Message"      value={selected.message} multiline />}
        {selected.motivation   && <DetailRow label="Motivation"   value={selected.motivation} multiline />}
        {selected.experience   && <DetailRow label="Experience"   value={selected.experience} multiline />}
        <DetailRow label="Applied" value={new Date(selected.created_at).toLocaleString()} />
      </>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
        {TABS.map(t => <button key={t.key} onClick={() => setTab(t.key)} style={tabBtn(t.key)}>{t.label}</button>)}
      </div>

      {/* Table card */}
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
          {pagination?.total ?? 0} records
        </div>
        <Table
          columns={tab === 'messages' ? MSG_COLS : APP_COLS}
          data={data}
          loading={loading}
          onRowClick={openDetail}
        />
        <Pagination pagination={pagination} onPage={setPage} />
      </div>

      {/* Detail / review modal */}
      {selected && (
        <Modal
          title={tab === 'messages' ? 'Message Detail' : 'Application Detail'}
          onClose={() => setSel(null)}
          footer={
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setSel(null)}>Cancel</Button>
              <Button variant="primary" onClick={handleUpdate} disabled={saving}>
                {saving ? <Spinner size={14} /> : 'Save Changes'}
              </Button>
            </div>
          }
        >
          {/* Submitted data */}
          <section style={{ marginBottom: 20 }}>
            {renderDetailBody()}
          </section>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Status selector */}
            <div>
              <label style={labelStyle}>Status</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                {statuses.map(s => (
                  <button
                    key={s}
                    onClick={() => setRF(f => ({ ...f, status: s }))}
                    style={{
                      padding: '5px 14px', borderRadius: 'var(--radius)', fontSize: 12,
                      cursor: 'pointer', textTransform: 'capitalize', fontWeight: 500,
                      border: `1.5px solid ${reviewForm.status === s ? 'var(--accent)' : 'var(--border)'}`,
                      background: reviewForm.status === s ? 'var(--accent-dim)' : 'transparent',
                      color: reviewForm.status === s ? 'var(--accent)' : 'var(--text-muted)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes (applications only) */}
            {tab !== 'messages' && (
              <div>
                <label style={labelStyle}>Internal Notes</label>
                <textarea
                  value={reviewForm.notes}
                  onChange={e => setRF(f => ({ ...f, notes: e.target.value }))}
                  rows={4}
                  placeholder="Add review notes visible only to admins…"
                  style={{
                    width: '100%', marginTop: 6, padding: '10px 12px',
                    background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', color: 'var(--text)', fontSize: 13,
                    resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5,
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── helpers ── */
const labelStyle = { fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)' };

function DetailRow({ label, value, multiline }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '6px 16px', marginBottom: 10, alignItems: multiline ? 'flex-start' : 'center' }}>
      <span style={labelStyle}>{label}</span>
      {multiline
        ? <p style={{ margin: 0, fontSize: 13, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{value}</p>
        : <span style={{ fontSize: 13, color: 'var(--text)' }}>{value}</span>
      }
    </div>
  );
}